import React, { useState, useEffect } from 'react';
import { fetchMapsLightweight, fetchMapById } from '../services/api';

/**
 * FeaturedMapsScroll - Galerie cu efect muzeu
 * - Lumină de muzeu în jurul fiecărei hărți
 * - Efect subtil de ridicare 3D la hover
 * - DOAR PE MOBIL (< 1024px)
 * - Imagini pre-încărcate pentru încărcare instantă
 */
const FeaturedMapsScroll = ({ onMapClick }) => {
  const [maps, setMaps] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Handle map click - fetch full data before opening modal
  const handleMapClick = async (map) => {
    if (!onMapClick || isLoading) return;
    
    setIsLoading(true);
    try {
      // Fetch full map data including description
      const fullMapData = await fetchMapById(map.id);
      onMapClick({ ...fullMapData });
    } catch (error) {
      console.error('Error fetching full map data:', error);
      // Fallback to lightweight data if fetch fails
      onMapClick({ ...map });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadMaps = async () => {
      try {
        const data = await fetchMapsLightweight();
        
        // Cele 3 hărți specifice dorite - ACUM CĂUTATE DUPĂ ID
        const wantedMaps = [
          { id: 'NZ8_8921', displayTitle: 'Turcici Imperii Descriptio' },
          { id: 'Z8W_9191.png', displayTitle: 'Transylvania - Hartă Mercator' },
          { id: 'Z8L_5028', displayTitle: 'DES KOENIGREICHS BULGARIEN Oestliche Hælffe' }
        ];
        
        // Găsim și redenumim hărțile pe baza ID-ului
        const filtered = wantedMaps
          .map(wanted => {
            // Căutăm exact după ID. Am adăugat și un fallback pentru .png în caz că ID-ul din DB nu conține extensia
            const found = data.find(m => m.id === wanted.id || m.id === wanted.id.replace('.png', ''));
            if (found) {
              return { ...found, title: wanted.displayTitle };
            }
            return null;
          })
          .filter(Boolean);
        
        const finalMaps = filtered.length > 0 ? filtered : data.slice(0, 3);
        setMaps(finalMaps);
        
        // Pre-încarcă imaginile
        if (finalMaps.length > 0) {
          const preloadPromises = finalMaps.map(map => {
            return new Promise((resolve) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = resolve; // Continue even if one fails
              img.src = map.imageUrl;
            });
          });
          
          await Promise.all(preloadPromises);
          setImagesLoaded(true);
        }
      } catch (error) {
        console.error('Error loading maps:', error);
      }
    };
    loadMaps();
  }, []);

  // Nu afișa componenta pe desktop sau dacă nu e gata
  if (!isMobile || maps.length === 0) return null;

  return (
    <section 
      className="featured-section"
      data-testid="featured-maps-scroll"
    >
      {/* Header */}
      <div className="section-header">
        <h2>Hărți Emblematice</h2>
        <p className="subtitle">3 hărți istorice selectate din colecția digitală de 80 hărți</p>
      </div>

      {/* Cards */}
      <div className="cards-container">
        {maps.map((map, index) => (
          <div
            key={map.id || index}
            data-testid={`featured-map-${index}`}
            className={`map-card ${isLoading ? 'loading' : ''}`}
            onClick={() => handleMapClick(map)}
            style={{ cursor: isLoading ? 'wait' : 'pointer' }}
          >
            {/* Museum spotlight - glow in jurul cardului */}
            <div className="spotlight" />
            
            {/* Imaginea cu info overlay */}
            <div className="card-image">
              <img
                src={map.imageUrl}
                alt={map.title || 'Hartă istorică'}
                loading="eager"
                decoding="async"
                fetchpriority="high"
                style={{ opacity: imagesLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
              />
              
              {/* Year badge */}
              <span className="year-badge">{map.year}</span>
              
              {/* Index */}
              <span className="index-badge">
                {String(index + 1).padStart(2, '0')}/{String(maps.length).padStart(2, '0')}
              </span>
              
              {/* Info overlay pe imagine */}
              <div className="info-overlay">
                <h3>{map.title}</h3>
                <p className="author">{map.author || 'Autor necunoscut'}</p>
                <span className="region">{(map.region || 'europa').replace(/-/g, ' ')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .featured-section {
          background: #5c4a3d;
          padding: 60px 20px;
        }

        .section-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .section-header h2 {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(255, 251, 235, 0.95);
          margin: 0;
          font-family: Georgia, serif;
        }

        .section-header .subtitle {
          font-size: 14px;
          color: rgba(255, 251, 235, 0.7);
          margin-top: 12px;
          font-weight: 400;
          letter-spacing: 0.03em;
        }

        .cards-container {
          max-width: 900px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 60px;
        }

        /* Card container */
        .map-card {
          position: relative;
          transition: transform 0.4s ease;
        }

        .map-card:hover {
          transform: translateY(-8px) scale(1.01);
        }

        /* Spotlight - glow foarte subtil */
        .spotlight {
          position: absolute;
          inset: -2px;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          pointer-events: none;
          z-index: 0;
        }

        /* Image container */
        .card-image {
          position: relative;
          z-index: 1;
          aspect-ratio: 16 / 9;
          overflow: hidden;
          border-radius: 10px;
        }

        .card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }

        .map-card:hover .card-image img {
          transform: scale(1.03);
        }

        .year-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(92, 74, 61, 0.85);
          backdrop-filter: blur(4px);
          color: #fbbf24;
          font-size: 11px;
          font-weight: 600;
          padding: 5px 12px;
          border-radius: 4px;
          z-index: 2;
        }

        .index-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          color: rgba(255, 255, 255, 0.8);
          font-size: 11px;
          font-family: monospace;
          background: rgba(92, 74, 61, 0.7);
          padding: 4px 8px;
          border-radius: 4px;
          z-index: 2;
        }

        /* Info overlay pe imagine - mai subtil */
        .info-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 30px 15px 12px;
          background: linear-gradient(to top, 
            rgba(92, 74, 61, 0.95) 0%, 
            rgba(92, 74, 61, 0.5) 60%,
            transparent 100%
          );
          z-index: 2;
        }

        .info-overlay h3 {
          font-family: Georgia, serif;
          font-size: 18px;
          font-weight: 500;
          color: #fff;
          margin: 0 0 6px;
          line-height: 1.3;
          text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }

        .info-overlay .author {
          font-size: 13px;
          color: rgba(255, 255, 255, 0.85);
          margin: 0 0 4px;
        }

        .info-overlay .region {
          font-size: 10px;
          color: rgba(255, 255, 255, 0.6);
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        /* Mobile specific */
        @media (max-width: 768px) {
          .cards-container {
            gap: 50px;
          }

          .info-overlay h3 {
            font-size: 15px;
          }

          .info-overlay .author {
            font-size: 12px;
          }
        }
      `}</style>
    </section>
  );
};

export default FeaturedMapsScroll;